import { ReservationProps } from '@expressable/syntax-ds';
import {
  addBusinessDays,
  differenceInMonths,
  differenceInYears,
  format,
  isDate,
  isValid,
  parse,
  parseISO,
} from 'date-fns';
import { formatInTimeZone } from 'domains/dates/format';
import { TimeZone } from 'domains/timezone';
import { TFunction } from 'i18next';

import { addDays, addHours, isWeekend } from 'date-fns';

// e.g. 2020-01-01
export const DATE_SHORT = 'yyyy-MM-dd';

// e.g. 01/02/2015
export const DATE_SHORT_HUMAN = 'MM/dd/yyyy';

// e.g. Feb 1, 2020
export const DATE_SHORT_MONTH = 'MMM d, yyyy';

// e.g. Saturday, February 1st
export const DATE_LONG_DAY_MONTH_ORDINAL = 'EEEE, MMMM do';

// e.g. Saturday, February 1, 2020
export const DATE_LONG_DAY_MONTH_YEAR = 'EEEE, MMMM d, yyyy';

// e.g. February 1, 2020
export const DAY_MONTH_YEAR = 'MMMM d, yyyy';

// e.g. Oct 5
export const MONTH_DAY_SHORT = 'MMM d';

// e.g. Saturday
export const WEEK_DAY_LONG = 'EEEE';

// e.g. Sat
export const WEEK_DAY_SHORT = 'EEE';

// e.g. 10:30 am
export const TIME_SHORT = 'h:mm a';

// e.g. 12am
export const TIME = 'h:mmaaa';

// e.g. 12am Eastern Standard Time (EST)
export const TIME_WITH_ZONE = 'h:mmaaa zzzz (zzz)';

// e.g. 12am (EST)
export const TIME_WITH_ZONE_SHORT = 'h:mmaaa (zzz)';

//eg: Tuesdays at 2:00 AM (CST)
export const TIME_WITH_ZONE_SHORT_WITH_DAY = 'EEEE h:mmaaa (zzz)';

// e.g. 2023-04-08T06:00:00-0700
export const ISO_FORMAT = `yyyy-LL-dd'T'HH:mm:ssXXXX`;

// e.g. Feb 2028
export const MONTH_YEAR = 'MMM yyyy';

// e.g. 9:00pm
export const TIME_ONLY = 'h:mmaaa';

// e.g April 16, 2025 at 2:30pm CST
export const DATE_LONG_DAY_MONTH_YEAR_TIME = 'MMMM d, yyyy';

// e.g. 2:30pm CST
export const TIME_ONLY_WITH_ZONE = 'h:mmaaa zzz';

// e.g. Central Standard Time (CST)
export const TIME_ZONE_ONLY = 'zzzzz (zzz)';

// e.g. 10:00 AM Central Standard Time (CST)
export const TIME_ONLY_WITH_ZONE_SHORT = 'h:mmaaa (zzz)';

// Ages below this will be displayed as e.g. "1 year and 5 months" while older ages will simply display as "3 years"
const MAX_AGE_TO_SHOW_MONTHS_IN_YEARS = 2;

export const getDaysInRange = (start: Date, count: number): Date[] => {
  const dates = [];
  const end = new Date();

  end.setDate(new Date().getDate() + count);

  for (let dt = start; dt <= end; dt.setDate(dt.getDate() + 1)) {
    const date = new Date(dt);

    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);

    dates.push(date);
  }

  return dates;
};

export const formatTimeWithZone = (date: Date, timeZone: TimeZone): string => {
  return formatInTimeZone(date, timeZone, TIME_WITH_ZONE);
};

export const formatTimeWithZoneShort = (date: Date, timeZone: TimeZone): string => {
  return formatInTimeZone(date, timeZone, TIME_WITH_ZONE_SHORT);
};

export const parseDateField = (_value: unknown, originalValue: string): Date | unknown => {
  return isDate(originalValue) ? originalValue : parse(originalValue, DATE_SHORT_HUMAN, new Date());
};

export const getPersonAge = (t: TFunction, dob?: Date) => {
  if (!dob) {
    return undefined;
  }
  const ageInMonths = differenceInMonths(new Date(), dob);
  return formatAgeFromMonths(ageInMonths, t);
};

export const isUnderage = (dob: string): boolean => {
  const today = new Date();
  const ageInYears = differenceInYears(today, dob);
  return ageInYears < 18;
};

export const formatAgeFromMonths = (ageInMonths: number, t: TFunction) => {
  const years = Math.floor(ageInMonths / 12);
  const reminder = ageInMonths % 12;

  if (ageInMonths === 0) {
    return t('screener.steps.enterDob.age.newborn');
  }

  if (ageInMonths < 0) {
    return '';
  }

  let result = '';

  if (years > 0) {
    result += `${years} ${t(`screener.steps.enterDob.age.${years > 1 ? 'years' : 'year'}`)}`;

    if (reminder > 0 && years < MAX_AGE_TO_SHOW_MONTHS_IN_YEARS) {
      result += ` ${t('screener.steps.enterDob.age.and')} `;
    }
  }

  if (reminder > 0 && years < MAX_AGE_TO_SHOW_MONTHS_IN_YEARS) {
    result += `${reminder} ${t(`screener.steps.enterDob.age.${reminder > 1 ? 'months' : 'month'}`)}`;
  }

  return result;
};

// To be used in Live Phone Calls using contentful configuration
/** Expects a rawDateRange with the format 09:30-11:45 and a valid timezone string */
export const isBetweenHours = (time: Date, rawDateRange: string, timezone: string): boolean => {
  // Split 09:30-11:45 into -> [[9,30] , [11,45]]
  const unparsedRanges = rawDateRange.split('-');

  if (unparsedRanges.length !== 2) {
    throw new Error(
      `Expected 2 strings after splitting at "-" but got ${unparsedRanges.length}. Raw value was ${rawDateRange}`,
    );
  }

  const parsedRanges = unparsedRanges.map(rawTime => rawTime.split(':').map(rawNumber => parseInt(rawNumber, 10)));

  if (parsedRanges.some(t => t.length !== 2)) {
    throw new Error(`Expected 2 strings after splitting at ":". Raw value was ${rawDateRange}`);
  }

  const startHours = parsedRanges[0][0];
  const startMinutes = parsedRanges[0][1];
  const endHours = parsedRanges[1][0];
  const endMinutes = parsedRanges[1][1];

  const currentHour = parseInt(formatInTimeZone(time, timezone, 'HH'), 10);
  const currentMinutes = parseInt(formatInTimeZone(time, timezone, 'mm'), 10);

  if (currentHour < startHours) {
    return false;
  }

  if (currentHour === startHours && currentMinutes < startMinutes) {
    return false;
  }

  if (currentHour > endHours) {
    return false;
  }

  if (currentHour === endHours && currentMinutes > endMinutes) {
    return false;
  }
  return true;
};

export const extractDate = (datetime: Date | string): string => {
  return typeof datetime === 'string' ? datetime.split('T')[0] : datetime.toISOString().split('T')[0];
};

type Params = {
  dateTime: Date;
  therapistName?: string;
  pluralize?: boolean;
  showStarting?: boolean;
  timezone?: TimeZone;
  formatFn: typeof format;
};

export const getReservationScheduleProps = ({
  dateTime,
  therapistName,
  pluralize = false,
  showStarting = false,
  timezone,
  formatFn,
}: Params): Pick<ReservationProps, 'dateLabel' | 'timeLabel'> => {
  const dayOfWeek = pluralize ? formatFn(dateTime, 'EEEE') : formatFn(dateTime, 'EEEE,');
  const monthDayYear = formatFn(dateTime, 'MMMM dd, yyyy');

  const timeString = timezone
    ? formatInTimeZone(dateTime, timezone, 'h:mm aa zzzz (zzz)')
    : formatFn(dateTime, 'h:mm aa zzzz (zzz)');

  const pluralSuffix = pluralize ? 's' : '';
  const startingSuffix = showStarting ? ' starting' : '';
  const therapistSuffix = therapistName ? ` with ${therapistName}` : '';

  const dateLabel = [dayOfWeek + pluralSuffix, startingSuffix, ' ', monthDayYear, therapistSuffix].join('');

  return {
    dateLabel,
    timeLabel: timeString,
  };
};

/** Used to avoid day offset when going from local timezone to UTC, in scenarios where API asks for UTC time but we dont care about hour/minutes
 * Will return todays date at mid-day UTC,
 */
export const getUTCMidDay = (date: Date = new Date()): Date => {
  return parseISO(`${format(date, 'yyyy-MM-dd')}T12:00:00Z`);
};

/** For dates of format MM/dd/yyyy
 * Will try to prevent day shifts due to lacking timezone and time in a DOB
 */
export const parseDOBToDate = (dob: string, dateFormat: string = 'MM/dd/yyyy'): Date => {
  return parse(`${dob} 12:00`, `${dateFormat} HH:mm`, new Date());
};

/**
 * Will check if a date string is correctly formatted in ISO 8601, exactly like the browser does
 * (Date.prototype.toISOString() is spec-defined and consistent)
 *
 *
 * Valid example: 2025-04-15T13:45:30.000Z
 * Invalid example: 2025-04-15T13:45:30Z (missing millisecond precision)
 */
export const isStrictISODate = (data: string): boolean => {
  const date = parseISO(data);
  return isValid(date) && date.toISOString() === data;
};

/**
 * Returns true if the provided dateTime is in the future compared to the current time.
 *
 * @param dateTime - A Date object or date/time string (ideally ISO 8601 format, e.g. '2025-04-15T14:00:00Z').
 *                   String inputs should include timezone info or be normalized to UTC to avoid timezone-related issues.
 *
 * @returns boolean - true if dateTime is in the future, false otherwise.
 *                    Returns false for empty strings, null, undefined, or invalid date formats.
 */
export const isInFuture = (dateTime: Date | string | null | undefined): boolean => {
  if (!dateTime) return false;

  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);

  // Check for invalid date (NaN timestamp)
  if (isNaN(date.getTime())) return false;

  return date.getTime() > Date.now();
};

export const getYearsOfAge = (dob: Date): number => {
  const today = new Date();
  const ageInYears = differenceInYears(today, dob);
  return ageInYears;
};

/** Adds hours to a date, but skips non-business days (weekends)
 * Example: Friday 12:00 add 24 hours -> Monday 12:00
 */
export const addBusinessHours = (date: Date, hours: number): Date => {
  const wholeDays = Math.floor(hours / 24);
  const remainderHours = hours % 24;

  // Step 1: add whole business days
  let result = addBusinessDays(date, wholeDays);

  // Step 2: add the remaining hours
  result = addHours(result, remainderHours);

  // Step 3: if the extra hours pushed us into a weekend, move to Monday
  if (isWeekend(result)) {
    const daysToAdd = result.getDay() === 6 ? 2 : 1; // Saturday → Monday, Sunday → Monday
    result = addDays(result, daysToAdd);
  }

  return result;
};
